<template>

    <accordion-input :accordion="accordion" :spacing="spacing" :open="open">
        <template #label>

            <div class="font-medium">{{ label }}</div>

        </template>
        <template #content>

            <small v-if="instruction" class="block mb-3 text-gray-500 text-muted">
                {{ instruction }}
            </small>

            <div class="mt-1">

                <button v-show="colors !== null"
                        v-for="(color, key) in colors"
                        :key="key"
                        :title="popoverEnabled ? null : swatchName(color, key)"
                        :style="{ background: color !== 'transparent' ? color : '' }"
                        :aria-disabled="isDisabled(color) ? 'true' : null"
                        class="relative inline-flex items-center justify-center w-8 h-8 mr-1 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-600 focus:border-blue-600 sm:text-sm align-middle"
                        :class="[
                            { selected: color === currentColor, checkered: color === 'transparent' },
                            isDisabled(color) ? 'cursor-not-allowed' : 'cursor-pointer',
                        ]"
                        @mouseenter="showPopover(color, key, $event)"
                        @mouseleave="hidePopover"
                        @focus="showPopover(color, key, $event)"
                        @blur="hidePopover"
                        @click="onSwatchClick(color)">
                    <!--
                        Disabled state: a translucent + greyscale overlay
                        covers the swatch's bg colour (so it reads as
                        "faded out" like the original opacity-30 grayscale
                        treatment), while the red ✕ icon sits ABOVE it at
                        full opacity. CSS `opacity` and `filter` on the
                        button itself would cascade to children, fading the
                        icon too — using a sibling overlay div lets the
                        icon stay vivid red.

                        Note: the swatch uses `aria-disabled`, NOT the native
                        `disabled` attribute. A natively-disabled <button>
                        fires no mouse/focus events, so the WCAG popover that
                        explains *why* it's blocked would be unreachable —
                        the very swatches that need the explanation. The
                        click guard lives in onSwatchClick() instead.
                    -->
                    <span v-if="isDisabled(color)"
                          class="absolute inset-0 rounded-md bg-white/60 backdrop-grayscale pointer-events-none"
                          aria-hidden="true"></span>
                    <i v-if="isDisabled(color)"
                       class="fas fa-times text-red-600 text-base relative drop-shadow-[0_0_2px_rgba(255,255,255,0.9)] pointer-events-none"
                       aria-hidden="true"></i>
                </button>

                <button v-show="colors === null"
                        v-for="index in 6"
                        :key="'skeleton-' + index"
                        class="inline-block w-8 h-8 mr-1 bg-gray-200 border border-gray-300 rounded-md shadow-sm cursor-not-allowed animate-pulse sm:text-sm"
                />

            </div>

            <!--
                Custom WCAG popover. Replaces the native `title` tooltip
                (browser-delayed, OS-styled) while a contrast pairing is
                active: it appears the instant the cursor enters a swatch
                and spells out the live ratio + why a swatch is blocked.
                position:fixed + viewport coords from getBoundingClientRect
                so it escapes the panel's `overflow-hidden` / scroller.
            -->
            <div v-if="popover"
                 class="colors-input-popover"
                 :class="popover.placement"
                 :style="{ top: popover.y + 'px', left: popover.x + 'px' }"
                 role="tooltip">

                <div class="cip-name">{{ popover.name }}</div>

                <div v-if="popover.ratio" class="cip-ratio">{{ popover.ratio }}</div>

                <div v-if="popover.reason" class="cip-reason">
                    <i class="fas fa-circle-xmark" aria-hidden="true"></i>
                    <span>{{ popover.reason }}</span>
                </div>

            </div>

        </template>
    </accordion-input>

</template>

<script>

import AccordionInput from "@galaxy/modules/Canvas/resources/js/editor/website/editors/inputs/helpers/Input";
import { isNumber } from 'lodash';
import { meetsContrast, contrastRatio } from "@galaxy/modules/Canvas/resources/js/editor/website/editors/inputs/helpers/contrast";

export default {

    components: {
        AccordionInput
    },

    props: {

        label: {
            type: String,
            required: true,
        },
        instruction: String,

        spacing: {
            horizontal: {
                type: Boolean,
                default: true
            },
            vertical: {
                type: Boolean,
                default: true
            }
        },

        accordion: {
            type: Boolean,
            default: true
        },

        open: {
            type: Boolean,
            default: null
        },

        options: {
            type: Object,
            required: true,
        },
        optionsProperty: {
            required: true,
        },

        colors: {
            type: Array|Object,
        },

        defaultColor: {},

        // When set, swatches that fail WCAG contrast against this sibling color
        // get greyed out and made unclickable (only while `window.galaxyWcag` is
        // true). Parent components — e.g. CommonInputs — pair the bg and text
        // pickers by passing each other's current value here.
        contrastWith: {
            type: String,
            default: null,
        },

        // Contrast target. 'AA' = 4.5:1 body text (default). 'AA_LARGE' = 3:1
        // for UI / ≥18pt text. 'AAA' = 7:1 stricter bar.
        contrastLevel: {
            type: String,
            default: 'AA',
        },
    },

    data() {
        return {
            isOpen: this.open || false,

            // Active swatch popover, or null. Shape:
            // { name, ratio, reason, x, y, placement }
            popover: null,
        }
    },

    methods: {
        isNumber,

        setValue(value) {
            // Set in options.
            this.$set(this.options, this.optionsProperty, value);

            // Emit for whoever wants to know.
            this.$emit('colorChange');
        },

        onSwatchClick(color) {
            if (this.isDisabled(color)) return;
            this.setValue(color);
        },

        // True when WCAG mode is on + a sibling color is set + this swatch
        // fails the AA ratio against it. Transparent / unknown colors never
        // disable — the contrast helper short-circuits to "pass".
        isDisabled(color) {
            if (!this.wcagEnabled || !this.contrastWith) return false;
            if (color === 'transparent') return false;
            return !meetsContrast(color, this.contrastWith, this.contrastLevel);
        },

        // Human label for a swatch — palette entries are keyed by name,
        // bare colour lists are keyed by index (fall back to the hex).
        swatchName(color, key) {
            return isNumber(key) ? color : key;
        },

        // Builds + shows the WCAG popover for the hovered/focused swatch.
        // No-op outside contrast mode — there the lightweight native
        // `title` (just the colour name) is left in place.
        showPopover(color, key, event) {
            if (!this.popoverEnabled) return;

            const rect = event.currentTarget.getBoundingClientRect();
            const ratio = color !== 'transparent'
                ? contrastRatio(color, this.contrastWith)
                : null;

            // Prefer above the swatch; flip below when too close to the
            // viewport top to render fully.
            const above = rect.top > 120;

            this.popover = {
                name: this.swatchName(color, key),
                ratio: ratio !== null
                    ? `Contrast ${ratio.toFixed(2)}:1 t.o.v. ${this.contrastWith}`
                    : null,
                reason: this.isDisabled(color)
                    ? `Niet selecteerbaar — minimaal ${this.contrastLevelLabel} vereist tegen de tegenkleur.`
                    : null,
                x: rect.left + rect.width / 2,
                y: above ? rect.top - 8 : rect.bottom + 8,
                placement: above ? 'above' : 'below',
            };
        },

        hidePopover() {
            this.popover = null;
        },

    },

    computed: {

        currentColor() {
            // Get value from options.
            let value = this.options[this.optionsProperty];

            // Use value of default value?
            return value === undefined ? this.defaultColor : value;
        },

        wcagEnabled() {
            return !!(typeof window !== 'undefined' && window.galaxyWcag);
        },

        // The custom popover only takes over while a contrast pairing is
        // active (WCAG on + a sibling colour to test against). Otherwise
        // there's nothing to explain and the native title suffices.
        popoverEnabled() {
            return this.wcagEnabled && !!this.contrastWith;
        },

        contrastLevelLabel() {
            if (this.contrastLevel === 'AAA') return 'AAA (7:1)';
            if (this.contrastLevel === 'AA_LARGE') return 'AA large (3:1)';
            return 'AA (4.5:1)';
        },

    },

}

</script>

<style lang="scss" scoped>

.selected {
    @apply shadow-sm ring-blue-600 border-blue-600;
}

.checkered {
    background:                linear-gradient(45deg, rgba(0, 0, 0, 0.0980392) 25%, transparent 25%, transparent 75%, rgba(0, 0, 0, 0.0980392) 75%, rgba(0, 0, 0, 0.0980392) 0), linear-gradient(45deg, rgba(0, 0, 0, 0.0980392) 25%, transparent 25%, transparent 75%, rgba(0, 0, 0, 0.0980392) 75%, rgba(0, 0, 0, 0.0980392) 0), white;
    background-repeat:         repeat, repeat;
    background-position:       0 0, 5px 5px;
    -webkit-transform-origin:  0 0 0;
    transform-origin:          0 0 0;
    -webkit-background-origin: padding-box, padding-box;
    background-origin:         padding-box, padding-box;
    -webkit-background-clip:   border-box, border-box;
    background-clip:           border-box, border-box;
    -webkit-background-size:   10px 10px, 10px 10px;
    background-size:           10px 10px, 10px 10px;
    -webkit-transform:         scaleX(1) scaleY(1) scaleZ(1);
    transform:                 scaleX(1) scaleY(1) scaleZ(1);
}

// WCAG swatch popover — fixed to the viewport, centred over the swatch.
// pointer-events:none so it never steals the hover it's describing.
.colors-input-popover {
    position: fixed;
    z-index: 99999;
    width: 230px;
    padding: 8px 10px;
    border-radius: 6px;
    background: #1f2937;
    color: #fff;
    font-size: 11px;
    line-height: 1.45;
    box-shadow: 0 6px 18px rgba(0, 0, 0, 0.28);
    pointer-events: none;

    &.above { transform: translate(-50%, -100%); }
    &.below { transform: translate(-50%, 0); }

    // Little arrow pointing at the swatch.
    &::after {
        content: '';
        position: absolute;
        left: 50%;
        margin-left: -5px;
        border: 5px solid transparent;
    }

    &.above::after {
        top: 100%;
        border-top-color: #1f2937;
    }

    &.below::after {
        bottom: 100%;
        border-bottom-color: #1f2937;
    }
}

.cip-name {
    font-weight: 600;
    word-break: break-word;
}

.cip-ratio {
    margin-top: 2px;
    color: #d1d5db;
}

.cip-reason {
    display: flex;
    gap: 5px;
    margin-top: 5px;
    padding-top: 5px;
    border-top: 1px solid rgba(255, 255, 255, 0.12);
    color: #fca5a5;

    i { margin-top: 1px; }
}

</style>
